import { useEffect, useState } from 'react'
import { API_URL, shippingFields as fields } from '../../constants'
import DialogModal from '../../shared/components/DialogModal'
import { FaCartShopping, FaCreditCard } from 'react-icons/fa6'
import { Button } from '../../shared/components/Button'
import { FaArrowLeft, FaPen } from 'react-icons/fa'
import { FormFieldset } from '../../shared/components/Fieldset'
import CurrencyFormat from './CurrencyFormat'
import CountrySelect from './CountrySelect'
import { CardElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCart } from '../../util/CartContext'
import { ErrorOverlay, LoadingOverlay } from '../../shared/components/Loading'
import { languageUtil } from '../../util/languageUtils'
import { useCurrency } from '../../util/CurrencyContext'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY)

const CartModal = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [shippingDetails, setShippingDetails] = useState({
        contact_method: 'sms',
        country: 'UK',
    })
    const [errors, setErrors] = useState({})
    const [error, setError] = useState({})
    const [clientSecret, setClientSecret] = useState(null)

    const [stage, setStage] = useState('cart') // 'cart' | 'shipping' | 'payment'
    const { fetchCartData, checkExistingToken, cart, deleteItem, isLoading, updateItem } = useCart()

    const cartToken = checkExistingToken()
    const { t } = useTranslation()
    const { currency } = useCurrency()
    const { language } = languageUtil()

    const cartItems = cart.items
    const total = cart.total

    const validateField = (fieldName, value) => {
        let error = null

        const requiredFields = [
            'first_name',
            'last_name',
            'street_address',
            'city',
            'postcode',
            'phone',
            'email',
        ]
        if (requiredFields.includes(fieldName) && (!value || value.trim() === '')) {
            return `${fieldName.replace('_', ' ')} is required`
        }

        // Specific field validations
        switch (fieldName) {
            case 'postcode':
                if (value && !/^\d+$/.test(value)) {
                    error = t('fields.shipping.errors.postcode')
                }
                break

            case 'phone':
                if (value) {
                    const digitsOnly = value.replace(/\D/g, '')
                    if (!/^\d+$/.test(digitsOnly)) {
                        error = t('fields.shipping.errors.phone')
                    } else if (digitsOnly.length < 10 || digitsOnly.length > 15) {
                        error = t('fields.shipping.errors.phone_digits')
                    }
                }
                break

            case 'email':
                if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error = t('fields.shipping.errors.email')
                }
                break
        }

        return error
    }

    const handleShippingChange = (fieldName, value) => {
        // Update shipping details
        setShippingDetails(prev => ({
            ...prev,
            [fieldName]: value,
        }))

        // Validate and update errors
        const error = validateField(fieldName, value)
        setErrors(prev => ({
            ...prev,
            [fieldName]: error || null,
        }))
    }

    const handleCart = async () => {
        setLoading(true)
        try {
            const res = await fetch(`${API_URL}/checkout/${cartToken}`, {
                headers: { 'Accept-Language': language },
            })
            const data = await res.json()
            const secret = data.data.clientSecret

            if (secret) {
                setClientSecret(secret)
                setStage('checkout')
            } else {
                setError({ message: 'Unable to get payment information.' })
            }
        } catch (err) {
            console.error('Error fetching client secret:', err)
            setError({ message: 'Something went wrong.' })
        } finally {
            setLoading(false)
        }
    }

    const validateAllFields = () => {
        const newErrors = {}
        let isValid = true

        // Check all required fields
        const requiredFields = [
            'first_name',
            'last_name',
            'street_address',
            'city',
            'postcode',
            'phone',
            'email',
        ]

        requiredFields.forEach(field => {
            const value = shippingDetails[field] || ''
            const error = validateField(field, value)
            if (error) {
                newErrors[field] = error
                isValid = false
            }
        })

        // Also validate optional fields if they have values
        const optionalFields = ['contact_method', 'donation_notes', 'gift_aid']
        optionalFields.forEach(field => {
            const value = shippingDetails[field]
            if (value) {
                // Only validate if there's a value
                const error = validateField(field, value)
                if (error) {
                    newErrors[field] = error
                    isValid = false
                }
            }
        })

        setErrors(newErrors)
        return isValid
    }

    const handleCheckout = () => {
        const isValid = validateAllFields()
        if (isValid) {
            setStage('payment')
        }
    }

    const handleDelete = async itemId => {
        try {
            await deleteItem(itemId)
        } catch (error) {
            console.error('Delete failed:', error)
        }
    }

    const handleUpdate = async (itemId, updateData) => {
        try {
            await updateItem(itemId, updateData)
        } catch (error) {
            console.error('Update failed:', error)
            throw error
        }
    }

    useEffect(() => {
        const token = cartToken
        if (isOpen) fetchCartData(token)
    }, [isOpen])

    const renderTitle =
        stage === 'checkout' ? (
            <div className='flex items-center gap-2'>
                <FaCartShopping /> {t('cart.checkout')}
            </div>
        ) : stage === 'cart' ? (
            <div className='flex items-center justify-center gap-8'>
                <div className='flex items-center gap-2'>
                    <FaCartShopping /> {t('cart.your_cart')}
                </div>
                {cartItems.length > 0 && (
                    <Button
                        variant='secondary'
                        size='small'
                        onClick={() => setIsEditMode(!isEditMode)}
                    >
                        <FaPen /> {isEditMode ? t('status.finish_edit') : t('status.edit')}
                    </Button>
                )}
            </div>
        ) : (
            <div className='flex items-center gap-2'>
                <FaCreditCard /> Payment
            </div>
        )

    return (
        <DialogModal isOpen={isOpen} onClose={onClose} title={renderTitle}>
            {error.message && (
                <div className='mb-2 text-sm text-center text-red-500'>{error.message}</div>
            )}

            {loading || isLoading ? (
                <p className='text-center text-gray-500'>{t('loading')}</p>
            ) : cartItems.length === 0 ? (
                <p className='text-center text-gray-500'>{t('cart.no_items')}</p>
            ) : stage === 'checkout' ? (
                <div className='flex flex-col gap-6 max-h-[70vh] overflow-y-auto px-1 sm:px-2'>
                    <div className='px-4 bg-white border rounded-lg shadow-sm'>
                        <ShippingForm
                            shippingDetails={shippingDetails}
                            errors={errors}
                            onChange={handleShippingChange}
                            onBack={() => {
                                setStage('cart')
                            }}
                            onSubmit={handleCheckout}
                        />
                    </div>
                </div>
            ) : stage === 'payment' ? (
                <div className='p-4 bg-white border rounded-lg shadow-sm'>
                    <h3 className='mb-4 text-lg font-semibold'>{t('cart.payment')}</h3>
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                        <StripePayment
                            clientSecret={clientSecret}
                            onBack={() => {
                                setStage('checkout')
                            }}
                            shippingDetails={shippingDetails}
                            cartToken={cartToken}
                            onFinish={success => {
                                setStage('cart')
                                onClose()
                            }}
                            currency={currency}
                        />
                    </Elements>
                </div>
            ) : (
                stage === 'cart' && (
                    <div className='flex flex-col md:flex-row md:gap-6 space-y-6 md:space-y-0 max-h-[70vh]'>
                        <CartItemList
                            items={cartItems}
                            isEditMode={isEditMode}
                            onDelete={handleDelete}
                            onUpdate={handleUpdate}
                        />
                        <CartSummary total={total} onCheckout={handleCart} />
                    </div>
                )
            )}
        </DialogModal>
    )
}

function StripePayment({ clientSecret, onBack, shippingDetails, cartToken, onFinish, currency }) {
    const { t } = useTranslation()

    const stripe = useStripe()
    const elements = useElements()
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)

    const navigate = useNavigate()
    const { language } = languageUtil()

    const handleSubmit = async e => {
        e.preventDefault()
        setLoading(true)
        setMessage(t('cart.processing'))

        // Early validation
        if (!stripe || !elements) {
            setLoading(false)
            return setErrorMessage(t('cart.error.stripe_not_initialized'))
        }

        const card = elements.getElement(CardElement)
        if (!card) {
            setLoading(false)
            return setErrorMessage(t('cart.error.no_card'))
        }

        try {
            const currencyResponse = await fetch(`${API_URL}/cart/${cartToken}/currency`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': language,
                },
                body: JSON.stringify({ currency }),
            })

            if (!currencyResponse.ok) {
                // setErrorMessage(t('cart.error.update_currency'))
                throw new Error(t('cart.error.update_currency'))
            }

            // const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
            //     clientSecret,
            //     {
            //         payment_method: {
            //             card,
            //             billing_details: {
            //                 name: `${shippingDetails.first_name} ${shippingDetails.last_name}`,
            //                 email: shippingDetails.email,
            //             },
            //         },
            //     }
            // )

            // if (stripeError) throw stripeError

            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card,
                billing_details: {
                    name: `${shippingDetails.first_name} ${shippingDetails.last_name}`,
                    email: shippingDetails.email,
                },
            })

            if (pmError) {
                // setErrorMessage(pmError)
                throw new Error(pmError)
            }

            const paymentResponse = await fetch(`${API_URL}/stripe-checkout/${cartToken}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': language,
                },
                body: JSON.stringify({
                    ...shippingDetails,
                    payment_method: paymentMethod.id,
                    // payment_method: setupIntent.payment_method,
                }),
            })

            if (!paymentResponse.ok) {
                // setErrorMessage(t('cart.error.payment_processing_failed'))
                throw new Error(t('cart.error.payment_processing_failed'))
            }

            // const result = await paymentResponse.json()
            // if (result.status !== 'success') {
            //     throw new Error(result.message || 'Payment failed')
            // }

            // const { error: stripeError } = await stripe.confirmCardPayment(
            //     { clientSecret: result.clientSecret },
            //     {
            //         payment_method: {
            //             card,
            //             billing_details: {
            //                 name: `${shippingDetails.first_name} ${shippingDetails.last_name}`,
            //                 email: shippingDetails.email,
            //             },
            //         },
            //     }
            // )

            // if (stripeError) throw stripeError

            const result = await paymentResponse.json()
            if (!paymentResponse.ok) {
                // backend might return requires_action with 402 for PaymentIntent requiring confirmation — handle it below
                // but if it's an error, show message
                if (
                    result &&
                    (result.status === 'requires_action' ||
                        result.clientSecret ||
                        result.payment_intent_client_secret)
                ) {
                    // continue to confirmation flow below
                } else {
                    // setErrorMessage(result.message || t('cart.error.payment_processing_failed'))
                    throw new Error(result.message || 'Payment processing failed')
                }
            }

            // The backend may return several shapes. Normalize secret:
            const secret =
                (result &&
                    (result.clientSecret ||
                        result.payment_intent_client_secret ||
                        result.paymentIntentClientSecret ||
                        result.payment_intent_client_secret)) ||
                null

            // If backend asks for client confirmation (one-time or subscription invoice) -> confirm with Stripe
            if (secret) {
                setMessage(t('cart.confirming_payment'))

                const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
                    secret,
                    {
                        payment_method: paymentMethod.id,
                    }
                )

                if (confirmError) {
                    // Payment failed or requires another method
                    // setErrorMessage(confirmError)
                    throw new Error(confirmError)
                }

                // PaymentIntent succeeded or no further action required
                // Treat as success
                localStorage.removeItem('cartToken')
                localStorage.removeItem('cartTokenExpiresAt')

                await Promise.allSettled([navigate('/payment-success'), onFinish?.(true)])
                return
            }

            // localStorage.removeItem('cartToken')
            // localStorage.removeItem('cartTokenExpiresAt')

            // await Promise.allSettled([navigate('/payment-success'), onFinish?.(true)])
        } catch (err) {
            console.error('Payment error:', err)
            setErrorMessage(err.message || t('cart.error.payment_failed'))

            // Use allSettled to ensure both actions complete
            await Promise.allSettled([navigate('/payment-failed'), onFinish?.(false)])
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className='mt-4 space-y-4'>
            <CardElement
                options={{
                    hidePostalCode: true,
                    style: {
                        base: {
                            color: '#32325d',
                            fontSize: '16px',
                            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                            fontSmoothing: 'antialiased',
                            '::placeholder': {
                                color: '#a0aec0',
                            },
                            padding: '12px 16px',
                            backgroundColor: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                        },
                        invalid: {
                            color: '#e53e3e',
                            iconColor: '#e53e3e',
                        },
                    },
                }}
                className='p-3 border border-gray-300 rounded-md'
            />

            <div className='flex items-end justify-between w-full pt-8'>
                <Button variant={loading ? 'disabled' : 'secondary'} size='large' onClick={onBack}>
                    <FaArrowLeft /> {t('cart.billing')}
                </Button>
                <Button
                    variant={loading ? 'disabled' : 'primary'}
                    size='large'
                    onClick={handleSubmit}
                >
                    <FaCreditCard /> {t('cart.submit')}
                </Button>
                {loading && <LoadingOverlay message={message} />}
                {errorMessage && (
                    <ErrorOverlay message={errorMessage} onClose={() => setErrorMessage(null)} />
                )}
            </div>
        </div>
    )
}

function ShippingForm({ shippingDetails, errors, onChange, onBack, onSubmit }) {
    const { t } = useTranslation()
    const handleInputChange = (fieldName, value) => {
        // Just pass the value to parent, no validation here
        onChange(fieldName, value)
    }

    // You can also add this function to validate on blur
    const handleBlur = (fieldName, value) => {
        onChange(fieldName, value)
    }

    return (
        <div className='overflow-y-auto max-h-fit'>
            <FormFieldset className='px-0' title={t('cart.billing_details')}>
                <CountrySelect onSelectCountry={country => onChange('country', country.code)} />

                {/* First Name */}
                <div className='mb-4'>
                    <label
                        htmlFor='first_name'
                        className='block mb-1 text-sm font-medium text-gray-700'
                    >
                        {t('fields.shipping.fields.first_name')}
                        <span className='text-red-500'> *</span>
                    </label>
                    <input
                        type='text'
                        id='first_name'
                        name='first_name'
                        value={shippingDetails.first_name || ''}
                        onChange={e => handleInputChange('first_name', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.first_name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                    />
                    {errors.first_name && (
                        <p className='mt-1 text-sm text-red-500'>{errors.first_name}</p>
                    )}
                </div>

                {/* Last Name */}
                <div className='mb-4'>
                    <label
                        htmlFor='last_name'
                        className='block mb-1 text-sm font-medium text-gray-700'
                    >
                        {t('fields.shipping.fields.last_name')}
                        <span className='text-red-500'> *</span>
                    </label>
                    <input
                        type='text'
                        id='last_name'
                        name='last_name'
                        value={shippingDetails.last_name || ''}
                        onChange={e => handleInputChange('last_name', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.last_name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                    />
                    {errors.last_name && (
                        <p className='mt-1 text-sm text-red-500'>{errors.last_name}</p>
                    )}
                </div>

                {/* Street Address */}
                <div className='mb-4'>
                    <label
                        htmlFor='street_address'
                        className='block mb-1 text-sm font-medium text-gray-700'
                    >
                        {t('fields.shipping.fields.street_address')}
                        <span className='text-red-500'> *</span>
                    </label>
                    <input
                        type='text'
                        id='street_address'
                        name='street_address'
                        value={shippingDetails.street_address || ''}
                        onChange={e => handleInputChange('street_address', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.street_address ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                    />
                    {errors.street_address && (
                        <p className='mt-1 text-sm text-red-500'>{errors.street_address}</p>
                    )}
                </div>

                {/* City */}
                <div className='mb-4'>
                    <label htmlFor='city' className='block mb-1 text-sm font-medium text-gray-700'>
                        {t('fields.shipping.fields.city')}
                        <span className='text-red-500'> *</span>
                    </label>
                    <input
                        type='text'
                        id='city'
                        name='city'
                        value={shippingDetails.city || ''}
                        onChange={e => handleInputChange('city', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.city ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                    />
                    {errors.city && <p className='mt-1 text-sm text-red-500'>{errors.city}</p>}
                </div>

                {/* Postcode Field */}
                <div className='mb-4'>
                    <label
                        htmlFor='postcode'
                        className='block mb-1 text-sm font-medium text-gray-700'
                    >
                        {t('fields.shipping.fields.postcode')}
                        <span className='text-red-500'> *</span>
                    </label>
                    <input
                        type='text'
                        id='postcode'
                        name='postcode'
                        value={shippingDetails.postcode || ''}
                        onChange={e => handleInputChange('postcode', e.target.value)}
                        onBlur={e => handleBlur('postcode', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.postcode ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                    />
                    {errors.postcode && (
                        <p className='mt-1 text-sm text-red-500'>{errors.postcode}</p>
                    )}
                </div>

                {/* Phone Field */}
                <div className='mb-4'>
                    <label htmlFor='phone' className='block mb-1 text-sm font-medium text-gray-700'>
                        {t('fields.shipping.fields.phone')}
                        <span className='text-red-500'> *</span>
                    </label>
                    <input
                        type='tel'
                        id='phone'
                        name='phone'
                        value={shippingDetails.phone || ''}
                        onChange={e => handleInputChange('phone', e.target.value)}
                        onBlur={e => handleBlur('phone', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                    />
                    {errors.phone && <p className='mt-1 text-sm text-red-500'>{errors.phone}</p>}
                </div>

                {/* Email Field */}
                <div className='mb-4'>
                    <label htmlFor='email' className='block mb-1 text-sm font-medium text-gray-700'>
                        {t('fields.shipping.fields.email')}
                        <span className='text-red-500'> *</span>
                    </label>
                    <input
                        type='email'
                        id='email'
                        name='email'
                        value={shippingDetails.email || ''}
                        onChange={e => handleInputChange('email', e.target.value)}
                        onBlur={e => handleBlur('email', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                    />
                    {errors.email && <p className='mt-1 text-sm text-red-500'>{errors.email}</p>}
                </div>

                {/* Contact Method (Select) */}
                <div className='mb-4'>
                    <label
                        htmlFor='contact_method'
                        className='block mb-1 text-sm font-medium text-gray-700'
                    >
                        {t('fields.shipping.fields.contact_method')}
                    </label>
                    <select
                        id='contact_method'
                        name='contact_method'
                        value={shippingDetails.contact_method || 'email'}
                        onChange={e => handleInputChange('contact_method', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.contact_method ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                        <option value='sms'>
                            {t('fields.shipping.options.contact_method.sms')}
                        </option>
                        <option value='email'>
                            {t('fields.shipping.options.contact_method.email')}
                        </option>
                    </select>
                    {errors.contact_method && (
                        <p className='mt-1 text-sm text-red-500'>{errors.contact_method}</p>
                    )}
                </div>

                {/* Donation Notes (Textarea) */}
                <div className='mb-4'>
                    <label
                        htmlFor='donation_notes'
                        className='block mb-1 text-sm font-medium text-gray-700'
                    >
                        {t('fields.shipping.fields.donation_notes')}
                    </label>
                    <textarea
                        id='donation_notes'
                        name='donation_notes'
                        value={shippingDetails.donation_notes || ''}
                        onChange={e => handleInputChange('donation_notes', e.target.value)}
                        rows={3}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.donation_notes ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    <p className='mt-1 text-sm text-gray-500'>
                        {t('fields.shipping.descriptions.donation_notes')}
                    </p>
                    {errors.donation_notes && (
                        <p className='mt-1 text-sm text-red-500'>{errors.donation_notes}</p>
                    )}
                </div>

                {/* Gift Aid (Checkbox) */}
                <div className='flex items-start'>
                    {/* Checkbox */}
                    <div className='flex items-center h-5'>
                        <input
                            type='checkbox'
                            id='gift_aid'
                            name='gift_aid'
                            checked={!!shippingDetails.gift_aid}
                            onChange={e => handleInputChange('gift_aid', e.target.checked)}
                            className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                        />
                    </div>

                    <div className='ml-3'>
                        <label htmlFor='gift_aid' className='cursor-pointer'>
                            <img
                                src='/gift_aid.jpg'
                                alt={t('fields.shipping.fields.gift_aid')}
                                className='w-auto h-6'
                            />
                            <span className='sr-only'>{t('fields.shipping.fields.gift_aid')}</span>
                        </label>
                        {shippingDetails.gift_aid && (
                            <>
                                <p className='mt-2 text-xs text-justify text-gray-700'>
                                    {t('fields.shipping.toc.1')}
                                </p>
                                <p className='mt-1 text-xs text-justify text-gray-700'>
                                    {t('fields.shipping.toc.2')}
                                </p>
                                <p className='mt-1 text-xs text-justify text-gray-700'>
                                    {t('fields.shipping.toc.3')}
                                </p>
                                <p className='mt-1 text-xs text-justify text-gray-700'>
                                    {t('fields.shipping.toc.4')}
                                </p>
                            </>
                        )}
                    </div>
                </div>

                <div className='flex items-end justify-between w-full pt-8'>
                    <Button variant='secondary' size='large' onClick={onBack}>
                        <FaArrowLeft /> {t('cart.title')}
                    </Button>
                    <Button type='button' variant='primary' size='large' onClick={onSubmit}>
                        {t('cart.to_payment')}
                    </Button>
                </div>
            </FormFieldset>
        </div>
    )
}

function CartItemList({ items, isEditMode, onDelete, onUpdate, showDelete = true }) {
    const { t } = useTranslation()
    const { currency, currencySymbol } = useCurrency()
    const [editingItem, setEditingItem] = useState(null)
    const [editForm, setEditForm] = useState({ quantity: 1, frequent_option: 'single' })
    const [isUpdating, setIsUpdating] = useState(false)

    // Reset editing when edit mode changes
    useEffect(() => {
        if (!isEditMode) {
            setEditingItem(null)
        }
    }, [isEditMode])

    const handleEditClick = item => {
        setEditingItem(item.id)
        setEditForm({
            quantity: item.quantity,
            frequent_option: item.frequent_option || 'single',
        })
    }

    const handleCancelEdit = () => {
        setEditingItem(null)
        setEditForm({ quantity: 1, frequent_option: 'single' })
    }

    const handleSaveEdit = async item => {
        if (!onUpdate) return

        setIsUpdating(true)
        try {
            await onUpdate(item.id, {
                quantity: editForm.quantity,
                frequent_option: editForm.frequent_option,
            })
            setEditingItem(null)
        } catch (error) {
            console.error('Update failed:', error)
        } finally {
            setIsUpdating(false)
        }
    }

    const handleInputChange = e => {
        const { name, value } = e.target
        setEditForm(prev => ({
            ...prev,
            [name]: name === 'quantity' ? parseInt(value) || 1 : value,
        }))
    }

    if (items.length === 0) {
        return (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
                <div className='flex items-center justify-center w-20 h-20 mb-4 rounded-full bg-gradient-to-br from-gray-100 to-gray-200'>
                    <svg
                        className='w-10 h-10 text-gray-400'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='1.5'
                            d='M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4'
                        ></path>
                    </svg>
                </div>
                <h3 className='mb-2 text-lg font-semibold text-gray-900'>{t('cart.no_items')}</h3>
                <p className='max-w-md text-gray-600'>{t('cart.no_items_subtitle')}</p>
            </div>
        )
    }

    return (
        <div className='w-full overflow-y-auto' style={{ maxHeight: '500px' }}>
            {items.map((item, index) => (
                <div
                    key={item.id || index}
                    className='relative p-5 transition-all duration-300 border group bg-gradient-to-br from-white to-amber-50 border-amber-100 rounded-xl hover:border-amber-300 hover:shadow-md'
                >
                    {/* Decorative corner */}
                    <div className='absolute w-2 h-2 rounded-full opacity-50 top-3 right-3 bg-amber-300'></div>

                    {/* Main content */}
                    <div className='flex items-start justify-between'>
                        <div className='flex-1 pr-4'>
                            {/* Category badge */}
                            {item.parent_class?.project_category_name && (
                                <div className='mb-2'>
                                    <span className='inline-block px-3 py-1 text-xs font-medium border rounded-full text-amber-700 bg-gradient-to-r from-amber-100 to-yellow-100 border-amber-200'>
                                        {item.parent_class.project_category_name.toUpperCase()}
                                    </span>
                                </div>
                            )}

                            {/* Project title */}
                            <h4 className='mb-2 text-lg font-semibold text-gray-900 line-clamp-2'>
                                {item.parent_class?.title || t('where_most_needed')}
                            </h4>

                            {/* Item details */}
                            <div className='flex flex-wrap items-center gap-4 text-sm text-gray-600'>
                                {editingItem === item.id ? (
                                    // Edit mode fields
                                    <div className='flex flex-wrap items-center gap-4'>
                                        <div className='flex flex-col gap-1'>
                                            <label className='text-xs text-gray-500'>
                                                {t('cart.options.frequency')}
                                            </label>
                                            <select
                                                name='frequent_option'
                                                value={editForm.frequent_option}
                                                onChange={handleInputChange}
                                                className='px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent'
                                                disabled={isUpdating}
                                            >
                                                <option value='single'>
                                                    {t('cart.options.frequent_options.single')}
                                                </option>
                                                <option value='monthly'>
                                                    {t('cart.options.frequent_options.monthly')}
                                                </option>
                                            </select>
                                        </div>

                                        <div className='flex flex-col gap-1'>
                                            <label className='text-xs text-gray-500'>
                                                {t('cart.options.quantity')}
                                            </label>
                                            <input
                                                type='number'
                                                name='quantity'
                                                min='1'
                                                value={editForm.quantity}
                                                onChange={handleInputChange}
                                                className='w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent'
                                                disabled={isUpdating}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    // Display mode
                                    <>
                                        <div className='flex items-center gap-1.5'>
                                            <svg
                                                className='w-4 h-4 text-amber-500'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                            >
                                                <path
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    strokeWidth='2'
                                                    d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                                                ></path>
                                            </svg>
                                            <span className='capitalize'>
                                                {item.frequent_option || 'One-time'} donation
                                            </span>
                                        </div>

                                        {item.quantity > 1 && (
                                            <div className='flex items-center gap-1.5'>
                                                <svg
                                                    className='w-4 h-4 text-blue-500'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    viewBox='0 0 24 24'
                                                >
                                                    <path
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                        strokeWidth='2'
                                                        d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                                                    ></path>
                                                </svg>
                                                <span>× {item.quantity}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Amount section */}
                        <div className='text-right min-w-[100px]'>
                            <div className='mb-1'>
                                <span className='text-xs text-gray-500'>{t('cart.amount')}</span>
                            </div>
                            <div className='text-xl font-bold text-gray-900'>
                                {item.original_currency === currency ? (
                                    <span>
                                        {currencySymbol}
                                        {item.original_amount}
                                    </span>
                                ) : (
                                    <CurrencyFormat amount={item.subtotal} />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className='pt-4 mt-4 border-t border-amber-100'>
                        {isEditMode && (
                            <div className='flex items-center gap-3'>
                                {editingItem === item.id ? (
                                    // Edit mode buttons
                                    <>
                                        <button
                                            onClick={() => handleSaveEdit(item)}
                                            disabled={isUpdating}
                                            className='flex items-center gap-2 px-4 py-2 font-medium text-white transition-all duration-300 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed'
                                        >
                                            {isUpdating ? (
                                                <>
                                                    <svg
                                                        className='w-4 h-4 animate-spin'
                                                        fill='none'
                                                        viewBox='0 0 24 24'
                                                    >
                                                        <circle
                                                            className='opacity-25'
                                                            cx='12'
                                                            cy='12'
                                                            r='10'
                                                            stroke='currentColor'
                                                            strokeWidth='4'
                                                        ></circle>
                                                        <path
                                                            className='opacity-75'
                                                            fill='currentColor'
                                                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                                        ></path>
                                                    </svg>
                                                    <span>Saving...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg
                                                        className='w-4 h-4'
                                                        fill='none'
                                                        stroke='currentColor'
                                                        viewBox='0 0 24 24'
                                                    >
                                                        <path
                                                            strokeLinecap='round'
                                                            strokeLinejoin='round'
                                                            strokeWidth='2'
                                                            d='M5 13l4 4L19 7'
                                                        ></path>
                                                    </svg>
                                                    <span>{t('cart.save_changes')}</span>
                                                </>
                                            )}
                                        </button>

                                        <button
                                            onClick={handleCancelEdit}
                                            disabled={isUpdating}
                                            className='px-4 py-2 font-medium text-gray-700 transition-all duration-300 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                                        >
                                            {t('cart.cancel')}
                                        </button>
                                    </>
                                ) : (
                                    // Normal edit mode buttons
                                    <>
                                        <button
                                            onClick={() => handleEditClick(item)}
                                            className='flex items-center gap-2 px-4 py-2 font-medium text-blue-600 transition-all duration-300 border border-blue-200 rounded-lg hover:text-blue-700 bg-gradient-to-r from-blue-50 to-sky-50 hover:from-blue-100 hover:to-sky-100 hover:border-blue-300'
                                        >
                                            <svg
                                                className='w-4 h-4'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                            >
                                                <path
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    strokeWidth='2'
                                                    d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                                                ></path>
                                            </svg>
                                            <span>{t('cart.edit_item')}</span>
                                        </button>

                                        {showDelete && (
                                            <button
                                                onClick={() => onDelete(item.id)}
                                                className='flex items-center gap-2 px-4 py-2 font-medium text-red-600 transition-all duration-300 border border-red-200 rounded-lg hover:text-red-700 bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 hover:border-red-300'
                                            >
                                                <svg
                                                    className='w-4 h-4 transition-transform group-hover/delete:scale-110'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    viewBox='0 0 24 24'
                                                >
                                                    <path
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                        strokeWidth='2'
                                                        d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                                                    ></path>
                                                </svg>
                                                <span>{t('cart.remove_item')}</span>
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Hover effect */}
                    <div className='absolute inset-0 transition-opacity duration-300 border-2 opacity-0 pointer-events-none border-amber-300 rounded-xl group-hover:opacity-100'></div>
                </div>
            ))}
        </div>
    )
}

function CartSummary({ total, onCheckout }) {
    const { t } = useTranslation()
    const { currency, currencySymbol, currencyRate } = useCurrency()

    return (
        <div className='flex flex-col justify-between w-full md:w-1/3'>
            <div className='p-4 border rounded-lg shadow-sm bg-gray-50'>
                <h3 className='mb-2 font-semibold text-gray-800'>{t('cart.summary')}</h3>

                <p className='text-gray-700'>
                    {t('cart.total')}:{' '}
                    <span className='font-bold text-gray-700'>
                        <CurrencyFormat amount={total} />
                    </span>
                </p>
                <div className='pt-2 mt-3 border-t border-gray-100'>
                    {currency !== 'GBP' && (
                        <p className='text-xs text-gray-500'>
                            {t('cart.exchangeRate')}:<br />
                            <span className='text-gray-700'>
                                1 GBP = {currencyRate.toFixed(4)} {currency}
                            </span>
                        </p>
                    )}
                </div>
            </div>
            <button
                className='w-full px-4 py-2 mt-6 font-semibold text-white uppercase transition-colors bg-yellow-600 rounded-lg hover:bg-yellow-600'
                onClick={onCheckout}
            >
                {t('cart.checkout')}
            </button>
        </div>
    )
}

export default CartModal
